import type { PropsWithChildren } from 'react';
import type React from 'react';
import { useContext, useMemo, useState } from 'react';

import { StickyActionBarHeightContext } from './context';

/**
 * Rendered height (in px) of the mobile sticky action bar (sign/share CTA), for use as a Waypoint
 * `bottomOffset`. See `StickyActionBarHeight['height']` in ./context for the full semantics.
 */
export function useStickyActionBarHeight(): number {
	const context = useContext(StickyActionBarHeightContext);

	if (!context) {
		throw new Error('StickyActionBarHeightContext was not set');
	}

	return context.height;
}

/**
 * Setter for the current sticky action bar height. Must have exactly one call site — the one
 * component that owns measuring the bar (`ActionButtonWrapper`). A second writer can race the
 * first and clobber its published value, since both would share this single context slot.
 */
export function useSetStickyActionBarHeight(): (height: number) => void {
	const context = useContext(StickyActionBarHeightContext);

	if (!context) {
		throw new Error('StickyActionBarHeightContext was not set');
	}

	return context.setHeight;
}

export const StickyActionBarHeightProvider: React.FC<PropsWithChildren<EmptyProps>> = ({ children }) => {
	// 0 is the correct standing value for a page that mounts no writer at all; one that does
	// overwrites this in a layout effect, before the first paint.
	const [height, setHeight] = useState(0);

	const value = useMemo(() => ({ height, setHeight }), [height]);

	return <StickyActionBarHeightContext.Provider value={value}>{children}</StickyActionBarHeightContext.Provider>;
};
